import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { PaperDarkBG } from "../kits";
import { loadZens } from "../data/loadZens";
import { loadArticles } from "../data/loadArticles";
import { ICON_IMAGE_URLS } from "./utils/iconImageMap";
import SplitListPreview from "./SplitListPreview";

const tabOptions = [
  { id: "youtube", label: "YouTube" },
  { id: "blog2", label: "Blog" },
  { id: "zendev", label: "Zen Dev" },
  { id: "Kurse", label: "Kurse" },
];

const validTabs = tabOptions.map((tab) => tab.id);

function normalizeCourseDescription(description) {
  if (!description) return "";
  if (Array.isArray(description)) return description[0] || "";
  if (typeof description === "string") {
    try {
      const parsed = JSON.parse(description);
      if (Array.isArray(parsed)) return parsed[0] || "";
    } catch (err) {
      return description;
    }
    return description;
  }
  return String(description);
}

function normalizeAssetUrl(value) {
  if (!value) return "";
  const str = String(value).trim();
  if (!str) return "";
  if (/^https?:\/\//i.test(str) || str.startsWith("data:")) return str;
  return str.startsWith("/") ? str : `/${str}`;
}

function extractYoutubeId(url = "") {
  if (!url) return "";
  const match = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([^?&/]+)/i
  );
  return match ? match[1] : "";
}

function youtubeThumb(url) {
  const id = extractYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function resolveCoursePreviewImage(course = {}) {
  const explicitImage =
    course.image ||
    course.cover ||
    course.thumbnail ||
    course.previewImage ||
    course.heroImage ||
    course.banner;

  if (explicitImage) return normalizeAssetUrl(explicitImage);

  const videoImage =
    youtubeThumb(course.videoUrl) ||
    youtubeThumb(course.video_url) ||
    youtubeThumb(course.youtubeUrl) ||
    youtubeThumb(course.youtube_url);

  if (videoImage) return videoImage;

  const iconImage = ICON_IMAGE_URLS[course.icon];
  if (iconImage) return iconImage;

  return "/images/b_gray-full.svg";
}

function sortByDateDesc(items = []) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a?.date || 0).getTime();
    const dateB = new Date(b?.date || 0).getTime();
    return dateB - dateA;
  });
}

export default function ZenLab2() {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    return validTabs.includes(tab) ? tab : "blog2";
  };

  const [activeTab, setActiveTab] = useState(getTabFromQuery);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState(null);
  const [youtubeFetched, setYoutubeFetched] = useState(false);

  const [zens, setZens] = useState([]);
  const [zensLoading, setZensLoading] = useState(false);
  const [zensError, setZensError] = useState(null);
  const [zensFetched, setZensFetched] = useState(false);

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [coursesFetched, setCoursesFetched] = useState(false);

  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState(null);
  const [blogFetched, setBlogFetched] = useState(false);

  const mobileTabRow = (
    <div className="flex w-full gap-1 px-1 pb-0">
      {tabOptions.map(({ id, label }) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 basis-0 h-10 px-2 rounded-t-2xl border border-b-0 text-[10px] font-mono tracking-[0.08em] whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-300 backdrop-blur-sm ${
              isActive
                ? "bg-[#dbd8cc]/95 text-[#111111] border-[#AC8E66] shadow-[0_10px_28px_rgba(0,0,0,0.28)]"
                : "bg-[#141414]/88 text-[#EFEBDC]/80 border-[#2a2a2a] hover:border-[#AC8E66]/60"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  useEffect(() => {
    const currentTab = getTabFromQuery();
    if (currentTab !== activeTab) setActiveTab(currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (activeTab !== params.get("tab")) {
      params.set("tab", activeTab);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    if (activeTab === "youtube") fetchYoutubeVideos();
    if (activeTab === "blog2") fetchBlogPosts();
    if (activeTab === "zendev") fetchZens();
    if (activeTab === "Kurse") fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchYoutubeVideos = async () => {
    if (youtubeFetched) return;
    setYtLoading(true);
    setYtError(null);
    try {
      const response = await axios.get(
        "https://www.googleapis.com/youtube/v3/playlistItems",
        {
          params: {
            playlistId: "PLXtZktdmGcVe2hLOmekArDhkf12DJ_kvS",
            key: "AIzaSyAeaZWjocSwQ70JMbYk-NU5TrHdVPmv6sk",
            part: "snippet",
            maxResults: 10,
          },
        }
      );

      const validVideos = response.data.items.filter(
        (video) =>
          video.snippet &&
          video.snippet.thumbnails?.high &&
          video.snippet.resourceId?.videoId
      );

      setYoutubeVideos(validVideos);
      setYoutubeFetched(true);
    } catch (err) {
      console.error("YouTube Fetch Error:", err.response?.data || err.message);
      setYtError("YouTube Videos konnten nicht geladen werden.");
    } finally {
      setYtLoading(false);
    }
  };

  const fetchZens = async () => {
    if (zensFetched) return;
    setZensLoading(true);
    setZensError(null);
    try {
      const data = await loadZens();
      setZens(data || []);
      setZensFetched(true);
    } catch (err) {
      console.error("Zen Fetch Error:", err);
      setZensError("Zen Dev konnte nicht geladen werden.");
    } finally {
      setZensLoading(false);
    }
  };

  const fetchBlogPosts = async () => {
    if (blogFetched) return;
    setBlogLoading(true);
    setBlogError(null);
    try {
      const data = await loadArticles();
      setBlogPosts(sortByDateDesc(data || []));
      setBlogFetched(true);
    } catch (err) {
      console.error("Blog Fetch Error:", err);
      setBlogError("Blog konnte nicht geladen werden.");
    } finally {
      setBlogLoading(false);
    }
  };

  const fetchCourses = async () => {
    if (coursesFetched) return;
    setCoursesLoading(true);
    setCoursesError(null);
    try {
      const response = await fetch("https://www.denisbitter.de/stage01/api/get_courses.php");
      const json = await response.json();
      if (!json?.success || !Array.isArray(json?.data)) {
        throw new Error("Ungültige Kursdaten");
      }
      setCourses(json.data);
      setCoursesFetched(true);
    } catch (err) {
      console.error("Course Fetch Error:", err);
      setCoursesError("Kurse konnten nicht geladen werden.");
    } finally {
      setCoursesLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "youtube":
        return (
          <SplitListPreview
            label="YouTube"
            command="ls youtube/"
            loading={ytLoading}
            error={ytError}
            introKicker="Video Wissen"
            introHeadline="Wissen und mehr in Videosprache."
            attachTop
            hideCommandOnMobile
            hideCommand
            items={youtubeVideos.map((video) => ({
              id: video.snippet.resourceId.videoId,
              title: video.snippet.title,
              subtitle: video.snippet.channelTitle || "YouTube Video",
              meta: "YouTube",
              date: video.snippet.publishedAt,
              image: video.snippet.thumbnails?.high?.url,
              href: `https://www.youtube.com/watch?v=${video.snippet.resourceId.videoId}`,
              external: true,
            }))}
          />
        );
      case "blog2":
        return (
          <SplitListPreview
            label="Posts"
            command="ls blog/"
            loading={blogLoading}
            error={blogError}
            introKicker="Blog Wissen"
            introHeadline="Inspirationen und mehr rund um digitales Design. Feed"
            attachTop
            hideCommandOnMobile
            hideCommand
            items={blogPosts.map((post) => ({
              id: `blog-${post.slug}`,
              title: post.title,
              subtitle: post.subtitle || "Design-Test im Magazin-Look",
              meta: post.subtitle || "Story",
              date: post.date,
              image: post.image || "/images/b_gray-full.svg",
              href: `/post/${post.slug}`,
              external: false,
            }))}
          />
        );
      case "Kurse":
        return (
          <SplitListPreview
            label="Kurse"
            command="ls kurse/"
            loading={coursesLoading}
            error={coursesError}
            introKicker="Lernpfade"
            introHeadline="Kurse mit Struktur, Praxis und klaren Projektergebnissen."
            attachTop
            hideCommandOnMobile
            hideCommand
            items={courses.map((course) => ({
              id: `course-${course.slug || course.id}`,
              title: course.title,
              subtitle: normalizeCourseDescription(course.description) || "Praxiskurs",
              meta: course.platform || "Kurs",
              date: "",
              image: resolveCoursePreviewImage(course),
              href: `/course/${course.slug || course.id}`,
              external: false,
            }))}
          />
        );
      case "zendev":
        return (
          <SplitListPreview
            label="Zen Dev"
            command="ls apps/"
            loading={zensLoading}
            error={zensError}
            introKicker="Zen Dev"
            introHeadline="Build in public: Tools, Workflows und echte Entwicklungswege."
            attachTop
            hideCommandOnMobile
            hideCommand
            items={zens.map((zen) => ({
              id: `zen-${zen.slug}`,
              title: zen.title,
              subtitle: zen.subtitle,
              meta: "Zen Dev",
              date: zen.date,
              image: zen.image,
              href: `/zen/${zen.slug}`,
              external: false,
            }))}
          />
        );
      default:
        return null;
    }
  };

  const activeHeaderMeta = (() => {
    switch (activeTab) {
      case "youtube":
        return {
          command: "ls youtube/",
          label: "youtube",
          count: youtubeVideos.length,
          loading: ytLoading,
          error: ytError,
        };
      case "blog2":
        return {
          command: "ls blog/",
          label: "posts",
          count: blogPosts.length,
          loading: blogLoading,
          error: blogError,
        };
      case "Kurse":
        return {
          command: "ls kurse/",
          label: "kurse",
          count: courses.length,
          loading: coursesLoading,
          error: coursesError,
        };
      case "zendev":
      default:
        return {
          command: "ls apps/",
          label: "zen dev",
          count: zens.length,
          loading: zensLoading,
          error: zensError,
        };
    }
  })();

  return (
    <PaperDarkBG fullHeight>
      <div className="pt-28 pb-6 px-2">
        <div className="max-w-6xl mx-auto rounded-t-2xl border border-b-0 border-[#2B2B2B] bg-[#141414]/92 backdrop-blur-[2px] px-4 sm:px-6 pt-5 pb-0">
          <p className="text-[11px] text-left font-mono text-[#EFEBDC]/70 pb-4 px-1 sm:px-2">
            ZenLab · <span className="text-[#AC8E66]">$</span> {activeHeaderMeta.command}
            <span
              className={`ml-2 ${
                activeHeaderMeta.error ? "text-red-400/80" : "text-green-400/70"
              }`}
            >
              &gt;{" "}
              {activeHeaderMeta.error
                ? "fehler beim laden."
                : activeHeaderMeta.loading
                  ? "laden..."
                  : `${activeHeaderMeta.count} ${activeHeaderMeta.label} gefunden.`}
            </span>
          </p>
          <div className="lg:hidden">
            {mobileTabRow}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-2 pb-10 flex gap-0">
        <aside className="hidden lg:flex flex-col pt-12 sticky 
        top-28 self-start z-20 pr-1">
          {tabOptions.map(({ id, label }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`w-8 h-32 -mb-1 rounded-l-xl border border-r-0 transition-all duration-300 backdrop-blur-sm ${
                  isActive
                    ? "translate-x-0 bg-[#dbd8cc]/95 text-[#111111] border-[#AC8E66] shadow-[0_10px_28px_rgba(0,0,0,0.28)] z-20"
                    : "-translate-x-1 bg-[#141414]/88 text-[#EFEBDC]/80 border-[#2a2a2a] hover:border-[#AC8E66]/60 hover:translate-x-0 z-10"
                }`}
                style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
              >
                <span className="tracking-[0.06em] text-[10px] font-mono">{label}</span>
              </button>
            );
          })}
        </aside>

        <main className="flex-1 min-w-0">
          {renderContent()}
        </main>
      </div>
    </PaperDarkBG>
  );
}
